import { type ChangeEvent, useEffect, useMemo, useState } from "react";

import { type CloudFlowNodeType, type GCPNodeConfigApiParameters } from "@doitintl/cmp-models";
import {
  Box,
  FormControl,
  FormControlLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { cloudflowTexts } from "../../../../assets/texts";
import VirtualizedAutocomplete from "../../../../Components/Autocomplete/VirtualizedAutocomplete";
import { useCustomerId } from "../../../../Components/hooks/useCustomerId";
import LoadingButton from "../../../../Components/LoadingButton";
import { useGetGcpProjects } from "../../hooks";
import { useGCPCloudPermissions } from "../ConfigurationPanel/hooks";
import { useNodeConfigurationContext } from "../ConfigurationPanel/NodeConfigurationContext";
import { PermissionCheckResults } from "../ConfigurationPanel/Tabs/PermissionCheckResults";
import { GCPHierarchicalResource, parseServiceAccountResource } from "../utils/gcpUtils";

const GCPPermissionForm = () => {
  const { nodeConfig, setActiveTab } = useNodeConfigurationContext<CloudFlowNodeType.ACTION>();
  const customerId = useCustomerId();

  const serviceAccountResource =
    (nodeConfig.parameters as GCPNodeConfigApiParameters).configurationValues.serviceAccountResource || "";
  const [resourceType, resourceId] = parseServiceAccountResource(serviceAccountResource);
  const organization = resourceType === "organizations" ? `organizations/${resourceId}` : "";

  const [hierarchicalResource, setHierarchicalResource] = useState<GCPHierarchicalResource>(
    resourceType === "organizations" ? GCPHierarchicalResource.ORGANIZATION : GCPHierarchicalResource.PROJECT
  );

  const [permissionsProjectId, setPermissionsProjectId] = useState<string>(
    resourceType === "projects" && resourceId ? resourceId : ""
  );

  const permissionConfigValues = useMemo(
    () => ({
      serviceAccountResource,
      hierarchicalResource,
      permissionsProjectId,
    }),
    [serviceAccountResource, hierarchicalResource, permissionsProjectId]
  );

  const { result, loading, updatePermissions } = useGCPCloudPermissions({
    permissionConfigValues,
  });

  const { data: projects, isLoading: gcpProjectsLoading } = useGetGcpProjects(customerId, organization, !!organization);

  const projectIds = useMemo(() => projects?.map((proj) => proj.projectId).filter(Boolean) || [], [projects]);

  const displayName = !serviceAccountResource
    ? "Not selected"
    : resourceType === "organizations"
      ? `Organization: ${resourceId}`
      : `Project: ${resourceId}`;

  const handleProjectIdChange = (value: string) => {
    setPermissionsProjectId(value);
  };

  const handleHierarchicalResourceChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newResource = event.target.value as GCPHierarchicalResource;
    setHierarchicalResource(newResource);
    if (newResource === GCPHierarchicalResource.ORGANIZATION) {
      handleProjectIdChange("");
    }
  };

  useEffect(() => {
    if (!hierarchicalResource) {
      setHierarchicalResource(GCPHierarchicalResource.ORGANIZATION);
    }
  }, [hierarchicalResource]);

  return (
    <>
      <Stack sx={{ gap: 2 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
            {cloudflowTexts.PERMISSIONS.CHECK_PERMISSIONS}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            {cloudflowTexts.PERMISSIONS.CHECK_PERMISSIONS_DESCRIPTION}
          </Typography>
        </Box>

        <Stack spacing={2}>
          <Stack direction="row" gap={1}>
            <Typography variant="body2">Selected resource:</Typography>
            <Typography variant="subtitle2">{displayName}</Typography>
          </Stack>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              {cloudflowTexts.PERMISSIONS.PERMISSION_CHECK_ON}
            </Typography>
            <RadioGroup
              style={{ marginLeft: 8 }}
              name="hierarchicalResource"
              value={hierarchicalResource}
              onChange={handleHierarchicalResourceChange}
            >
              <FormControlLabel
                value={GCPHierarchicalResource.ORGANIZATION}
                control={<Radio size="small" />}
                label={<Typography variant="body2">Organization</Typography>}
                disabled={!serviceAccountResource || resourceType !== "organizations"}
              />
              <FormControlLabel
                disabled={!serviceAccountResource}
                value={GCPHierarchicalResource.PROJECT}
                control={<Radio size="small" />}
                label={<Typography variant="body2">Project</Typography>}
              />
            </RadioGroup>
          </Box>

          {organization && hierarchicalResource === GCPHierarchicalResource.PROJECT && (
            <FormControl fullWidth>
              <VirtualizedAutocomplete
                id="project"
                loading={gcpProjectsLoading}
                options={projectIds}
                value={permissionsProjectId || null}
                onChange={(_, newValue) => {
                  handleProjectIdChange(newValue as string);
                }}
                onInputChange={(_, newValue) => {
                  handleProjectIdChange(newValue);
                }}
                renderOption={(props, option) => (
                  <MenuItem {...props} value={option}>
                    {option}
                  </MenuItem>
                )}
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    type="text"
                    label="Project ID"
                    name="Project"
                    fullWidth
                    variant="outlined"
                    required
                    helperText="Select a project for permission validation"
                  />
                )}
                size="small"
                fullWidth
              />
            </FormControl>
          )}
        </Stack>

        {!serviceAccountResource && (
          <Typography variant="body2" color="text.secondary">
            Please select an organization or project in the{" "}
            <Link
              variant="body2"
              underline="hover"
              onClick={() => {
                setActiveTab("Parameters");
              }}
              style={{ cursor: "pointer" }}
            >
              {cloudflowTexts.PERMISSIONS.PARAMETERS_TAB}
            </Link>
          </Typography>
        )}
      </Stack>
      <LoadingButton
        disabled={hierarchicalResource === GCPHierarchicalResource.PROJECT && !permissionsProjectId}
        variant="outlined"
        onClick={updatePermissions}
        loading={loading}
        mixpanelEventId="cloudflow.check-permissions"
        sx={{ alignSelf: "flex-start" }}
      >
        Check for required permissions
      </LoadingButton>
      <PermissionCheckResults nodeConfig={nodeConfig} loading={loading} result={result} />
    </>
  );
};

export default GCPPermissionForm;
